/**
 * Shared ownership-icon column (`<OwnershipBadge>`).
 *
 * TNCY-6b promoted this from `features/projects/columns.tsx`; TNCY-9 promotes
 * it into the cross-app `@aeriosiq/ownership` package so every merged
 * cross-tenant list prepends the SAME row tell. Not sortable/filterable —
 * it's a row tell, not data. The row only needs the optional ownership
 * fields (`ownership` / `ownerTenantName` / `role`); rows without them
 * render the subtle "owner" affordance.
 */
import { Box } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import { OwnershipBadge, type Ownership } from "./OwnershipBadge.js";
import type { AccessRole } from "./roleLabels.js";

/**
 * Screen-reader-only text. The column is a 44px icon strip with no visible
 * header by design, but a `role="columnheader"` with no discernible text is an
 * a11y gap (axe `empty-table-header`) — a screen-reader user tabbing the header
 * row hears nothing and cannot tell what the column is for.
 */
const SR_ONLY = {
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
} as const;

export interface OwnershipRow {
  ownership?: Ownership;
  ownerTenantName?: string | null;
  role?: AccessRole | null;
}

export function ownershipColumn<R extends OwnershipRow>(opts?: {
  /** Owner-tooltip noun passed through to the badge ("project" | "contract"). */
  entityNoun?: string;
}): GridColDef<R> {
  const entityNoun = opts?.entityNoun;
  return {
    field: "__ownership",
    // Kept empty so DataGrid renders no visible header text — the 44px icon
    // strip is deliberately unlabelled visually. The accessible name comes from
    // `renderHeader` below instead, which keeps the layout byte-for-byte while
    // giving screen readers something to announce.
    headerName: "",
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    width: 44,
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <Box component="span" sx={SR_ONLY}>
        Origin
      </Box>
    ),
    renderCell: (p) => (
      <OwnershipBadge
        ownership={p.row.ownership}
        ownerTenantName={p.row.ownerTenantName}
        role={p.row.role}
        {...(entityNoun !== undefined ? { entityNoun } : {})}
      />
    ),
  };
}
